import {
  CheckCircle,
  ChevronRight,
  Clock,
  RefreshCcw01,
  XClose,
} from '@timelyapp/icons/line-icons';
import { HelpCircle } from '@timelyapp/icons/solid-icons';
import { Dropdown, RadixTooltip, Spinner } from '@timelyapp/react';
import type { ProjectSuggestion } from 'api/agentSessions/projectSuggestions.api';
import { DateTime } from 'luxon';
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type ReactNode,
  useContext,
  useRef,
  useState,
} from 'react';
import { ProviderIcon } from './AgentIdentity';
import type { ProjectOption } from './AgentProjectPopover';
import styles from './AgentProjectPopover.module.scss';
import type { AgentPeriod } from './agentTracking.model';
import {
  hasRoomForProjectDetails,
  ProjectPickerAcceptedContext,
  ProjectPickerDetailsContext,
} from './ProjectPickerDetailsContext';
import { ProjectSuggestionEvidence } from './ProjectSuggestionEvidence';
import {
  averageProjectScore,
  closeProjectChoice,
  hasDisplayableProject,
  leadingProjectGroup,
  projectChoiceGroup,
  projectChoices,
  unmatchedPeriods,
} from './projectSuggestionChoices';
import type { CheckProgress, ReviewState } from './SessionProjectSuggestions';

export const canRequestProjectSuggestion = (p: AgentPeriod) =>
  !!p.summary?.trim() &&
  p.projectPredictionState !== 'waiting_summary' &&
  !(p.projectAssignmentSource === 'inherited' && !p.projectPredictionState) &&
  !['queued', 'running'].includes(p.projectPredictionState ?? '');

export const canPredictProject = (p: AgentPeriod) =>
  canRequestProjectSuggestion(p) &&
  (p.projectId === null || ['inherited', 'automatic'].includes(p.projectAssignmentSource ?? ''));

const predictionAttentionLabels: Record<string, string> = {
  failed: 'Project prediction failed',
  limit_reached: 'Daily prediction limit reached',
  stale: 'Project suggestion is out of date',
  delayed: 'Project prediction is delayed',
  no_projects: 'No available projects for prediction',
};

const progressLabels: Record<CheckProgress, string> = {
  finding: 'Finding a project…',
  still: 'Still checking…',
  slow: 'Taking longer than usual…',
};

/**
 * Work in flight: a spinner, plus words while a requested check runs, so a slow
 * check reads as working rather than stuck. Shorter work keeps its label for assistive tech only.
 */
function PredictionStatus({ review }: { review: ReviewState | null | undefined }) {
  const busy = review?.busy;
  const progress = review?.progress;
  return (
    <span role="status" className={styles.predictionProgress}>
      {busy && (
        <>
          <span aria-hidden="true" className={styles.predictionSpinner}>
            <Spinner size={12} />
          </span>
          {progress ? (
            <span className={styles.progressLabel}>{progressLabels[progress]}</span>
          ) : (
            <span className={styles.visuallyHidden}>{busy}</span>
          )}
        </>
      )}
    </span>
  );
}

/** What the person needs to know about the last request, and what they can do about it. */
function ReviewMessages({ review, error }: { review: ReviewState | null; error?: string }) {
  const message = review?.error || error;
  return (
    <>
      {review?.progress === 'slow' && (
        <p className={styles.muted}>
          You can close this. We’ll keep checking and highlight the project button when the
          suggestion is ready.
        </p>
      )}
      {review?.notice && (
        <p role="status" className={styles.muted}>
          {review.notice}
        </p>
      )}
      {message && (
        <p role="alert">
          {message}
          {review?.retry && message === review.error && (
            <>
              {' '}
              <button
                type="button"
                className={styles.textButton}
                disabled={!!review.busy}
                onClick={review.retry}
              >
                Try again
              </button>
            </>
          )}
        </p>
      )}
    </>
  );
}

export function predictionIndicator(periods: AgentPeriod[], review: ReviewState | null) {
  if (review?.error) return { symbol: '!', label: 'Project suggestion needs attention' };
  if (review?.progress) return { symbol: '◷', label: 'Checking project…', busy: true };
  const attention = periods
    .map(p => predictionAttentionLabels[p.projectPredictionState ?? ''])
    .find(Boolean);
  if (attention) return { symbol: '!', label: attention };
  if (
    review?.busy ||
    periods.some(p => ['queued', 'running'].includes(p.projectPredictionState ?? ''))
  )
    return { symbol: '◷', label: 'Finding project…', busy: true };
  if (
    periods.some(p => {
      if (review) {
        const suggestion = review.suggestions[p.segmentId];
        return (
          !!suggestion && hasDisplayableProject(suggestion) && p.projectId !== suggestion.project_id
        );
      }
      return p.projectPredictionState === 'review' || p.projectAssignmentSource === 'automatic';
    })
  )
    return review?.arrived
      ? { symbol: '✧', label: 'Project suggestion ready', fresh: true }
      : { symbol: '✧', label: 'Project suggestion' };
  if (periods.some(p => p.projectPredictionState === 'waiting_summary'))
    return { symbol: '◷', label: 'Waiting for summary' };
  if (periods.some(p => p.projectAssignmentSource === 'inherited' && !p.projectPredictionState))
    return { symbol: '◷', label: 'Project check pending' };
  return undefined;
}

export function AgentProjectPredictions({
  periods,
  review,
  onAssignPeriod,
  projects = [],
  compact = false,
}: {
  periods: AgentPeriod[];
  review: ReviewState | null;
  projects?: ProjectOption[];
  compact?: boolean;
  onAssignPeriod?: (period: AgentPeriod, id: number | null) => Promise<void>;
}) {
  const [error, setError] = useState('');
  const [undoing, setUndoing] = useState(false);
  const waiting = periods.filter(p => p.projectPredictionState === 'waiting_summary');
  const eligible = periods.filter(canPredictProject);
  const pending = periods.filter(
    p =>
      p.projectAssignmentSource === 'inherited' &&
      (!p.projectPredictionState || p.projectPredictionState === 'waiting_summary')
  );
  const checking = periods.some(p =>
    ['queued', 'running'].includes(p.projectPredictionState ?? '')
  );
  const incomplete = periods.some(p =>
    ['failed', 'limit_reached', 'stale', 'no_projects', 'delayed'].includes(
      p.projectPredictionState ?? ''
    )
  );
  const inherited = periods.some(p => p.projectAssignmentSource === 'inherited');
  const automatic = periods.filter(p => p.projectAssignmentSource === 'automatic');
  const results = periods.filter(p => review?.suggestions[p.segmentId]);
  const predicted = results.filter(p => hasDisplayableProject(review!.suggestions[p.segmentId]));
  const groups = [
    ...new Set(predicted.map(p => projectChoiceGroup(review!.suggestions[p.segmentId]))),
  ];
  const groupMembers = (id: number | null) =>
    predicted.filter(p => projectChoiceGroup(review!.suggestions[p.segmentId]) === id);
  // A session is usually one project: accepting its leading suggestion also
  // covers the periods that could not get a suggestion of their own.
  const unmatched = review && groups.length ? unmatchedPeriods(periods, review.suggestions) : [];
  const fillGroup = unmatched.length
    ? leadingProjectGroup(groups.map(id => ({ id, members: groupMembers(id) })))
    : undefined;
  const unclear = results.filter(
    p => p.projectId === null && !hasDisplayableProject(review!.suggestions[p.segmentId])
  );
  const missing = eligible.filter(
    p =>
      !review?.suggestions[p.segmentId] &&
      !(
        p.projectAssignmentSource === 'inherited' &&
        ['confirmed', 'kept'].includes(p.projectPredictionState ?? '')
      )
  );
  const onlyUnassigned =
    missing.length > 0 &&
    missing.every(p => p.projectId === null) &&
    periods.some(p => p.projectId !== null);
  const missingSummaries = periods.filter(
    p =>
      p.projectId === null &&
      !p.summary?.trim() &&
      !review?.suggestions[p.segmentId] &&
      !(p.projectAssignmentSource === 'inherited' && !p.projectPredictionState) &&
      !['waiting_summary', 'queued', 'running'].includes(p.projectPredictionState ?? '')
  );
  const waitingAlongsideSuggestion = predicted.length > 0 && waiting.length > 0 && !inherited;
  const waitingNote = (
    <p className={styles.waitingNote} role="status">
      {waiting.length === 1
        ? `${waitingAlongsideSuggestion ? '1 other period is' : 'This period is'} waiting for its summary.`
        : `${waiting.length}${waitingAlongsideSuggestion ? ' other' : ''} periods are waiting for their summaries.`}
    </p>
  );
  const waitingNotice =
    waiting.length > 0 && !inherited && !waitingAlongsideSuggestion ? (
      compact ? (
        waitingNote
      ) : (
        <div className={styles.projectCheckStatus} role="status">
          <Clock aria-hidden="true" />
          <div>
            <strong>Waiting for summary</strong>
            <p>
              {waiting.length === 1
                ? 'AI will suggest a project automatically when the activity summary is ready.'
                : `AI will suggest projects automatically when the summaries for ${waiting.length} periods are ready.`}
            </p>
          </div>
        </div>
      )
    ) : null;
  const summaryNotice = (
    <>
      {waitingNotice}
      {review && missingSummaries.length > 0 ? (
        <p className={styles.muted}>
          {missingSummaries.length === periods.length
            ? 'Activity summary unavailable. Project suggestions need an activity summary.'
            : `${missingSummaries.length} ${missingSummaries.length === 1 ? 'period has' : 'periods have'} no activity summary and cannot receive project suggestions yet.`}
        </p>
      ) : null}
    </>
  );
  const busy = !!review?.busy || undoing;
  if (!review && !inherited && !automatic.length) return null;
  if (waiting.length > 0 && waiting.length === periods.length && !inherited && !results.length) {
    return (
      <section className={styles.waitingSummary} aria-label="Project predictions">
        {waitingNotice}
      </section>
    );
  }
  const manuallyAssigned =
    periods.length > 0 &&
    periods.every(
      p =>
        p.projectId !== null &&
        !['inherited', 'automatic'].includes(p.projectAssignmentSource ?? '')
    );
  if (review && manuallyAssigned && !results.length) {
    const requestable = periods.filter(canRequestProjectSuggestion);
    return (
      <section className={styles.generateSuggestion} aria-label="Project predictions">
        <div className={styles.predictionActionRow}>
          <button
            type="button"
            className={styles.textButton}
            disabled={busy || !requestable.length}
            onClick={() => void review.generate(requestable.map(p => p.segmentId))}
          >
            <span aria-hidden="true">✧</span> Generate suggestion
          </button>
          <PredictionStatus review={review} />
        </div>
        {summaryNotice}
        <ReviewMessages review={review} />
        {review.canUndo && (
          <button
            type="button"
            className={styles.textButton}
            disabled={busy}
            onClick={() => void review.undo()}
          >
            Undo acceptance
          </button>
        )}
      </section>
    );
  }
  if (
    review &&
    !results.length &&
    !pending.length &&
    !checking &&
    !inherited &&
    !automatic.length &&
    missing.length > 0
  ) {
    const action = onlyUnassigned
      ? `Suggest for ${missing.length} unassigned ${missing.length === 1 ? 'period' : 'periods'}`
      : incomplete
        ? 'Retry prediction'
        : 'Suggest project';
    return (
      <section className={styles.generateSuggestion} aria-label="Project predictions">
        <div className={styles.predictionActionRow}>
          <RadixTooltip
            tooltip={
              onlyUnassigned
                ? `Find a project for ${missing.length === 1 ? 'the unassigned period' : `the ${missing.length} unassigned periods`}. Already assigned periods won’t change.`
                : 'Uses activity summaries, session context and previous assignments.'
            }
            side="top"
            delayDuration={0}
          >
            <button
              type="button"
              className={styles.suggestionPrompt}
              aria-label={action}
              disabled={busy}
              onClick={() => void review.generate(missing.map(p => p.segmentId))}
            >
              <span className={styles.promptSparkle} aria-hidden="true">
                ✧
              </span>
              {compact ? (incomplete ? 'Retry prediction' : 'Suggest project') : action}
            </button>
          </RadixTooltip>
          <PredictionStatus review={review} />
        </div>
        {summaryNotice}
        <ReviewMessages review={review} />
        {review.canUndo && (
          <button
            type="button"
            className={styles.textButton}
            disabled={busy}
            onClick={() => void review.undo()}
          >
            Undo acceptance
          </button>
        )}
      </section>
    );
  }
  const confirmed = periods.some(p => p.projectPredictionState === 'confirmed');
  const checkStatus = inherited ? (
    <div className={styles.projectCheckStatus} role={checking ? 'status' : undefined}>
      {confirmed && !pending.length && !checking ? (
        <CheckCircle aria-hidden="true" />
      ) : (
        <Clock aria-hidden="true" />
      )}
      <div>
        <strong>
          {checking
            ? 'Checking project…'
            : pending.length
              ? 'Project check pending'
              : confirmed
                ? 'Project confirmed'
                : 'Using this session’s project'}
        </strong>
        <p>
          {checking
            ? 'Your current assignment stays in place.'
            : pending.length
              ? 'We’ll check this project once the activity ends and its final summary is ready.'
              : confirmed
                ? 'The check matched the project already assigned.'
                : 'Carried forward from this session’s project. You can change it below.'}
        </p>
      </div>
    </div>
  ) : null;
  const onlyInheritanceStatus =
    inherited &&
    periods.every(p => p.projectId !== null) &&
    !automatic.length &&
    !incomplete &&
    !review?.error &&
    !review?.canUndo &&
    !missing.length &&
    results.every(p => {
      const suggestion = review?.suggestions[p.segmentId];
      return (
        !suggestion || !hasDisplayableProject(suggestion) || suggestion.project_id === p.projectId
      );
    });
  if (onlyInheritanceStatus) {
    return (
      <section className={styles.projectCheckSummary} aria-label="Project status">
        {checkStatus}
      </section>
    );
  }
  const undoAutomatic = async (period: AgentPeriod) => {
    setError('');
    setUndoing(true);
    try {
      await onAssignPeriod?.(period, null);
    } catch (e) {
      setError((e as Error).message || 'Could not undo assignment');
    } finally {
      setUndoing(false);
    }
  };
  return (
    <>
      <section
        className={compact ? styles.inlinePredictions : styles.predictions}
        aria-label="Project predictions"
      >
        <div className={styles.predictionHeading}>
          <span aria-hidden="true">✧</span>{' '}
          {inherited || checking ? 'Project check' : compact ? 'Suggestion' : 'Project suggestion'}
          <div className={styles.predictionRefreshRow}>
            {review && results.some(canRequestProjectSuggestion) && (
              <RadixTooltip tooltip="Refresh suggestions" side="top" delayDuration={0}>
                <button
                  type="button"
                  className={styles.refreshButton}
                  aria-label="Refresh suggestions"
                  disabled={busy}
                  onClick={() =>
                    void review.generate(
                      results.filter(canRequestProjectSuggestion).map(p => p.segmentId)
                    )
                  }
                >
                  <RefreshCcw01 width={15} height={15} aria-hidden="true" />
                </button>
              </RadixTooltip>
            )}{' '}
            <PredictionStatus review={review} />
          </div>
        </div>
        {checkStatus}
        {!inherited && checking && (
          <div className={styles.predictionProgress} role="status">
            <Spinner size={12} /> Finding project…
          </div>
        )}
        {incomplete && (
          <p className={styles.muted}>Project check incomplete. Retry below or choose a project.</p>
        )}
        {automatic.map(p => (
          <p key={p.segmentId}>
            Automatically assigned
            {automatic.length > 1 ? ` · ${p.date}` : ''}.{' '}
            {onAssignPeriod && (
              <button
                className={styles.textButton}
                type="button"
                disabled={busy}
                onClick={() => void undoAutomatic(p)}
              >
                Undo{automatic.length > 1 ? ` period ${periods.indexOf(p) + 1}` : ''}
              </button>
            )}
          </p>
        ))}
        {groups.map(id => {
          const members = groupMembers(id);
          return (
            <div key={id} className={styles.predictionGroup}>
              <ProjectChoiceList
                periods={members}
                fill={id === fillGroup ? unmatched : undefined}
                review={review!}
                disabled={busy}
                projects={projects}
                explanation={(choice, applicable) => (
                  <ProjectEvidencePopup
                    projectName={choice.project_name}
                    project={projects.find(p => p.id === choice.project_id)}
                  >
                    <ProjectSuggestionEvidence
                      entries={applicable.map(p => ({
                        sessionId: p.sessionId,
                        periodId: p.segmentId,
                        suggestion: review!.suggestions[p.segmentId],
                      }))}
                    >
                      {detailed => (
                        <ProjectEvidence
                          choice={choice}
                          periods={applicable}
                          suggestions={detailed}
                        />
                      )}
                    </ProjectSuggestionEvidence>
                  </ProjectEvidencePopup>
                )}
              />
            </div>
          );
        })}
        {unclear.length > 0 && fillGroup === undefined && (
          <p className={styles.muted}>
            No clear project match for {unclear.length}{' '}
            {unclear.length === 1 ? 'period' : 'periods'}. Choose a project below.
          </p>
        )}
        {fillGroup === undefined && summaryNotice}
        {!waiting.length &&
          !missingSummaries.length &&
          !results.length &&
          !pending.length &&
          !checking &&
          !inherited &&
          !automatic.length && (
            <p className={styles.muted}>
              {onlyUnassigned
                ? `Find a project for ${missing.length === 1 ? 'the unassigned period' : `the ${missing.length} unassigned periods`}. Already assigned periods won’t change.`
                : eligible.length
                  ? 'Find a project using the summaries, session context and your previous assignments.'
                  : 'Choose a project below.'}
            </p>
          )}
        <div className={styles.actions}>
          {review && missing.length > 0 && (
            <button
              type="button"
              className={styles.secondary}
              disabled={busy}
              onClick={() => void review.generate(missing.map(p => p.segmentId))}
            >
              {onlyUnassigned
                ? `Suggest for ${missing.length} unassigned ${missing.length === 1 ? 'period' : 'periods'}`
                : results.length
                  ? 'Suggest remaining'
                  : incomplete
                    ? 'Retry prediction'
                    : 'Suggest project'}
            </button>
          )}
          {review?.canUndo && (
            <button
              type="button"
              className={styles.textButton}
              disabled={busy}
              onClick={() => void review.undo()}
            >
              Undo acceptance
            </button>
          )}
        </div>
        <ReviewMessages review={review} error={error} />
      </section>
      {waitingAlongsideSuggestion && waitingNote}
    </>
  );
}

type EvidenceExample = NonNullable<ProjectSuggestion['evidence']>['assignment_history'][number];

const periodReference = (p: AgentPeriod) => `session:${p.sessionId}/period:${p.segmentId}`;
const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;
const shortDay = (day: string) =>
  new Date(`${day}T00:00`).toLocaleDateString([], { month: 'short', day: 'numeric' });

/** The session's provider mark beside its title, as in the sessions table. */
function SessionTitle({ period, title }: { period: AgentPeriod; title: string }) {
  return (
    <span className={styles.sessionTitle}>
      {period.provider && (
        <ProviderIcon provider={period.provider} name={period.providerName} selectable />
      )}
      <span>{title}</span>
    </span>
  );
}

/** One fact the model used, readable on its own, opening to the evidence behind it. */
function EvidenceRow({
  label,
  headline,
  children,
}: {
  label: string;
  headline: ReactNode;
  children?: ReactNode;
}) {
  const heading = (
    <span className={styles.evidenceRowHeading}>
      <span className={styles.evidenceRowLabel}>{label}</span>
      <span>{headline}</span>
    </span>
  );
  if (!children) return <div className={styles.evidenceRow}>{heading}</div>;
  return (
    <details className={styles.evidenceRow}>
      <summary>
        <ChevronRight width={16} height={16} aria-hidden="true" />
        {heading}
      </summary>
      <div className={styles.evidenceRowBody}>{children}</div>
    </details>
  );
}

/** A period or example on one line; opening it shows the full summary. */
function EvidenceItem({
  meta,
  tag,
  text,
  suggested,
}: {
  meta?: string;
  tag?: ReactNode;
  text: string;
  suggested?: boolean;
}) {
  return (
    <details className={styles.evidenceItem} data-suggested={suggested || undefined}>
      <summary>
        {meta && <span className={styles.evidenceItemMeta}>{meta}</span>}
        <span className={styles.evidenceItemMain}>
          {tag}
          <span className={styles.evidenceItemText}>{text}</span>
        </span>
      </summary>
      <p>{text}</p>
    </details>
  );
}

// The picker is scoped to one session. Each period's prediction saw its own copy of
// the session, its earlier periods and a sample of past choices; they mostly
// overlap, so the explanation merges them into one view instead of repeating them.
function ProjectEvidence({
  choice,
  periods,
  suggestions,
}: {
  choice: { project_id: number; project_name: string };
  periods: AgentPeriod[];
  suggestions: Record<number, ProjectSuggestion>;
}) {
  const project = choice.project_name;
  const evidence = periods.map(p => suggestions[p.segmentId]?.evidence);
  const { title, folder_name, ...rest } = evidence.find(Boolean)?.session_context ?? {};
  const details = [
    ...(folder_name?.trim() ? [['Folder', folder_name] as const] : []),
    ...Object.entries(rest)
      .filter(([, value]) => value.trim())
      .map(
        ([key, value]) =>
          [key.replaceAll('_', ' ').replace(/^./, c => c.toUpperCase()), value] as const
      ),
  ];
  const unique = (items: EvidenceExample[]) => [
    ...new Map(items.map(item => [item.reference, item])).values(),
  ];
  const suggestedReferences = new Set(periods.map(periodReference));
  const earlier = unique(evidence.flatMap(e => e?.prior_session_periods ?? [])).filter(
    item => !suggestedReferences.has(item.reference)
  );
  const assignedEarlier = earlier.filter(item => item.project_name !== null);
  const matchingEarlier = assignedEarlier.filter(item => item.project_name === project).length;
  const past = unique(evidence.flatMap(e => e?.assignment_history ?? [])).filter(
    item =>
      !suggestedReferences.has(item.reference) && !earlier.some(e => e.reference === item.reference)
  );
  const pastByProject = [
    ...past
      .reduce((groups, item) => {
        const name = item.project_name ?? 'Unassigned';
        groups.set(name, [...(groups.get(name) ?? []), item]);
        return groups;
      }, new Map<string, EvidenceExample[]>())
      .entries(),
  ].sort(([a, x], [b, y]) =>
    a === project ? -1 : b === project ? 1 : y.length - x.length || a.localeCompare(b)
  );
  const matchingPast = pastByProject.find(([name]) => name === project)?.[1].length ?? 0;
  const score = (p: AgentPeriod) =>
    projectChoices(suggestions[p.segmentId]).find(c => c.project_id === choice.project_id)
      ?.probability;
  const average = averageProjectScore(periods.map(score));
  // Rails sends example times in UTC. Show them in the offset the session's own
  // periods were recorded in, so every row reads on the same clock as the table.
  const zone = DateTime.fromISO(periods[0].startedAt, { setZone: true }).zone;
  const recorded = (iso: string) => DateTime.fromISO(iso).setZone(zone);
  const timeline = [
    ...earlier.map(item => ({
      key: item.reference,
      at: item.started_at,
      time: item.started_at ? recorded(item.started_at).toFormat('HH:mm') : undefined,
      day: item.started_at ? (recorded(item.started_at).toISODate() ?? undefined) : undefined,
      text: item.period_summary,
      suggested: false,
      tag: item.project_name ?? 'Unassigned',
    })),
    ...periods.map(p => ({
      key: periodReference(p),
      at: p.startedAt,
      // As recorded, like the rest of the table, rather than in this browser's zone.
      time: p.start as string | undefined,
      day: p.date as string | undefined,
      text: suggestions[p.segmentId]?.evidence?.period_summary ?? p.summary ?? '',
      suggested: true,
      tag: `✧ Suggested · ${averageProjectScore([score(p)]) ?? 'no score'}`,
    })),
  ].sort((a, b) => (a.at && b.at ? Date.parse(a.at) - Date.parse(b.at) : 0));
  // A session can cross midnight; then times alone would read out of order.
  const spansDays = new Set(timeline.map(item => item.day).filter(Boolean)).size > 1;
  const activity =
    earlier.length === 0
      ? 'No earlier activity in this session'
      : assignedEarlier.length === 0
        ? `${plural(earlier.length, 'earlier period')}, none assigned yet`
        : matchingEarlier === assignedEarlier.length && matchingEarlier > 1
          ? `All ${matchingEarlier} earlier assigned periods are ${project}`
          : `${matchingEarlier} of ${plural(assignedEarlier.length, 'earlier assigned period')} ${matchingEarlier === 1 ? 'is' : 'are'} ${project}`;
  const generated = periods
    .map(p => suggestions[p.segmentId]?.generated_at)
    .filter((at): at is string => !!at)
    .sort((a, b) => Date.parse(a) - Date.parse(b))
    .at(-1);
  return (
    <div>
      <p className={styles.evidenceVerdict}>
        {periods.length === 1
          ? `Suggested for this period · ${average ?? 'no'} model score`
          : `Suggested for ${periods.length} periods in this session · ${average ?? 'no'} average model score`}
      </p>
      <div className={styles.evidenceRows}>
        <EvidenceRow
          label="This session"
          headline={<SessionTitle period={periods[0]} title={title?.trim() || periods[0].title} />}
        >
          {details.length > 0 && (
            <dl className={styles.evidenceDetails}>
              {details.map(([name, value]) => (
                <div key={name}>
                  <dt>{name}</dt>
                  <dd>{value}</dd>
                </div>
              ))}
            </dl>
          )}
        </EvidenceRow>
        <EvidenceRow
          label={`Activity in this session · ${plural(timeline.length, 'period')}`}
          headline={activity}
        >
          {timeline.map(item => (
            <EvidenceItem
              key={item.key}
              meta={
                item.time &&
                (spansDays && item.day ? `${shortDay(item.day)} ${item.time}` : item.time)
              }
              tag={
                <span className={styles.evidenceTag} data-suggested={item.suggested || undefined}>
                  {item.tag}
                </span>
              }
              text={item.text}
              suggested={item.suggested}
            />
          ))}
        </EvidenceRow>
        {past.length > 0 && (
          <EvidenceRow
            label={`Your past choices · ${plural(past.length, 'example')}`}
            headline={`${matchingPast} of ${plural(past.length, 'past example')} went to ${project}`}
          >
            {pastByProject.map(([name, items]) => (
              <details key={name} className={styles.evidenceGroup}>
                <summary>
                  <ChevronRight width={14} height={14} aria-hidden="true" />
                  <span>{name}</span>
                  <span className={styles.muted}>{items.length}</span>
                </summary>
                {items.map(item => (
                  <EvidenceItem
                    key={item.reference}
                    meta={item.session_context?.title?.trim() || undefined}
                    text={item.period_summary}
                  />
                ))}
              </details>
            ))}
          </EvidenceRow>
        )}
      </div>
      {generated && (
        <p className={styles.muted}>
          Based on what we knew on{' '}
          {new Date(generated).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
          })}
          .
        </p>
      )}
    </div>
  );
}

// Forward the popover trigger props to the button, through the tooltip wrapper.
const ProjectExplanationTrigger = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement>
>((props, ref) => {
  const restoringFocus = useRef(false);
  return (
    <RadixTooltip tooltip="Why this project?" side="top" delayDuration={0}>
      <button
        {...props}
        ref={ref}
        type="button"
        className={styles.evidenceButton}
        aria-label="Why this project?"
        onClick={event => {
          restoringFocus.current = true;
          props.onClick?.(event);
        }}
        onFocus={event => {
          props.onFocus?.(event);
          // Returning from the explanation must not reopen a tooltip and consume Escape.
          if (restoringFocus.current) {
            event.preventDefault();
            restoringFocus.current = false;
          }
        }}
      >
        <HelpCircle width={18} height={18} aria-hidden="true" />
      </button>
    </RadixTooltip>
  );
});
ProjectExplanationTrigger.displayName = 'ProjectExplanationTrigger';

function ProjectEvidencePopup({
  projectName,
  project,
  children,
}: {
  projectName: string;
  project?: ProjectOption;
  children: ReactNode;
}) {
  const projectLabel = (
    <div className={styles.evidenceProject}>
      <i
        className={styles.dot}
        aria-hidden="true"
        style={
          {
            '--project-color': project?.color ? `#${project.color.replace(/^#/, '')}` : undefined,
          } as CSSProperties
        }
      />
      <div>
        {projectName}
        {project?.client && (
          <>
            {' '}
            <small>({project.client})</small>
          </>
        )}
      </div>
    </div>
  );
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<{ side: 'left' | 'right'; offset: number }>({
    side: 'right',
    offset: 12,
  });
  const showDetails = useContext(ProjectPickerDetailsContext);
  const trigger = useRef<HTMLButtonElement>(null);
  const changeOpen = (next: boolean) => {
    const panel = trigger.current?.closest('[data-project-picker-panel]')?.getBoundingClientRect();
    if (
      next &&
      panel &&
      showDetails &&
      !hasRoomForProjectDetails(panel.left, panel.right, window.innerWidth)
    ) {
      showDetails({
        projectName,
        projectLabel,
        content: children,
        returnFocus: () => trigger.current?.focus(),
      });
      return;
    }
    const button = trigger.current?.getBoundingClientRect();
    if (next && panel && button) {
      const side = window.innerWidth - panel.right >= 448 ? 'right' : 'left';
      setPlacement({
        side,
        offset: 12 + (side === 'right' ? panel.right - button.right : button.left - panel.left),
      });
    }
    setOpen(next);
  };
  return (
    <Dropdown
      open={open}
      onOpenChange={changeOpen}
      align="start"
      side={placement.side}
      sideOffset={placement.offset}
      variant="popover"
      showArrow={false}
      className={styles.evidenceSurface}
      aria-label={`Why ${projectName}?`}
      trigger={<ProjectExplanationTrigger ref={trigger} />}
    >
      <header className={styles.heading}>
        <div>
          <strong>Why this project?</strong>
          {projectLabel}
        </div>
        <button type="button" aria-label="Close project explanation" onClick={() => setOpen(false)}>
          <XClose width={18} height={18} />
        </button>
      </header>
      <div className={styles.evidenceBody}>{children}</div>
    </Dropdown>
  );
}

function ProjectChoiceList({
  periods,
  fill,
  review,
  disabled,
  explanation,
  projects,
}: {
  periods: AgentPeriod[];
  /** Unassigned periods without their own suggestion, offered alongside this group. */
  fill?: AgentPeriod[];
  review: ReviewState;
  disabled: boolean;
  explanation: (
    choice: { project_id: number; project_name: string },
    applicable: AgentPeriod[]
  ) => ReactNode;
  projects: ProjectOption[];
}) {
  const onAccepted = useContext(ProjectPickerAcceptedContext);
  const result = review.suggestions[periods[0].segmentId];
  const candidates = periods.flatMap(p => projectChoices(review.suggestions[p.segmentId]));
  const choices = [...new Map(candidates.map(c => [c.project_id, c])).values()]
    .sort((a, b) => {
      if (a.project_id === result.project_id) return -1;
      if (b.project_id === result.project_id) return 1;
      const score = (id: number) =>
        Math.max(...candidates.filter(c => c.project_id === id).map(c => c.probability ?? 0));
      return score(b.project_id) - score(a.project_id);
    })
    .slice(0, 3);
  const isClose = (id: number) =>
    periods.some(p => closeProjectChoice(review.suggestions[p.segmentId], id));
  const matchesCurrentProject = periods.every(
    p => p.projectId === review.suggestions[p.segmentId].project_id
  );
  const visible = choices.filter(c => isClose(c.project_id));
  const others = choices.filter(c => !isClose(c.project_id));
  const renderChoice = (choice: (typeof choices)[number]) => {
    const applicable = periods.filter(p =>
      projectChoices(review.suggestions[p.segmentId]).some(c => c.project_id === choice.project_id)
    );
    const scores = applicable.map(
      p =>
        projectChoices(review.suggestions[p.segmentId]).find(
          c => c.project_id === choice.project_id
        )?.probability
    );
    const score = averageProjectScore(scores);
    const changes = applicable.filter(p => p.projectId !== choice.project_id);
    const filled = changes.length ? (fill ?? []) : [];
    const total = changes.length + filled.length;
    const project = projects.find(p => p.id === choice.project_id);
    return (
      <div className={styles.choiceRow} key={choice.project_id}>
        <div className={styles.suggestionProject}>
          <span
            className={styles.dot}
            aria-hidden="true"
            style={
              {
                '--project-color': project?.color
                  ? `#${project.color.replace(/^#/, '')}`
                  : undefined,
              } as CSSProperties
            }
          />
          <div>
            <div className={styles.suggestionName}>
              <strong>{choice.project_name}</strong>
              {project?.client && (
                <>
                  {' '}
                  <small>({project.client})</small>
                </>
              )}
            </div>
            <div
              className={styles.muted}
              title={
                applicable.length > 1
                  ? `Average model score across ${applicable.length} periods, weighted equally`
                  : undefined
              }
            >
              {score && `${score} model score`}
              {applicable.length < periods.length &&
                ` · ${applicable.length} of ${periods.length} periods`}
            </div>
          </div>
        </div>
        <div className={styles.choiceActions}>
          {explanation(choice, applicable)}
          {changes.length > 0 && (
            <button
              type="button"
              className={visible.length > 1 ? styles.secondary : styles.primary}
              disabled={disabled}
              aria-label={`Accept ${choice.project_name} for ${total} ${total === 1 ? 'period' : 'periods'}`}
              onClick={async () => {
                const accepted = await review.accept(
                  changes.map(p => p.segmentId),
                  choice.project_id === result.project_id ? undefined : choice.project_id,
                  filled.map(p => p.segmentId)
                );
                if (accepted) onAccepted?.();
              }}
            >
              Accept
            </button>
          )}
        </div>
      </div>
    );
  };
  return (
    <>
      {visible.length > 1 && !matchesCurrentProject && (
        <p className={styles.muted}>Close match — choose a project</p>
      )}
      {matchesCurrentProject ? (
        <p className={styles.muted}>Matches current project — no action needed.</p>
      ) : periods.some(
          p => p.projectId !== null && p.projectId !== review.suggestions[p.segmentId].project_id
        ) ? (
        <p className={styles.muted}>Suggested change</p>
      ) : null}
      {visible.map(renderChoice)}
      {others.length > 0 && (
        <details className={styles.otherChoices}>
          <summary>
            {others.length} other {others.length === 1 ? 'suggestion' : 'suggestions'}
          </summary>
          {others.map(renderChoice)}
        </details>
      )}
    </>
  );
}
